import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, renderTitle, requireImageUrl, brandInitials, PHOTO_GRADE } from "./types";

// venueFramed — editorial "gallery print" layout. Unlike the four full-bleed
// templates, the graded photo is INSET inside a generous warm-paper mat
// (passe-partout) with the venue name set in serif beneath it, like a framed
// print or a magazine plate. The light paper ground means the brand mark is
// dark-on-light (no whitening), the opposite of the photo-over-dark layouts.
// Best for a calm, premium "portfolio" pin that lets the photo breathe.
const PAPER = "#f3efe8"; // near-white warm beige (texture is white grain)
const INK = "#2b2520";
const PAPER_TEX = "https://pins-sauntera.web.app/stock/band-texture-white.jpg";

export function venueFramedTemplate(input: PinInput): string {
  const { brand, brandLogo, cta, typography, venue } = input;
  const imageUrl = requireImageUrl(input, "venueFramed");
  if (!venue) {
    throw new Error("venueFramed template requires venue { name, location?, tagline? }");
  }

  // Caption sits below the photo with the full mat width; same ~2-line target.
  const nameLen = venue.name.length;
  const nameSize = nameLen <= 22 ? 78 : nameLen <= 32 ? 64 : nameLen <= 42 ? 54 : 46;

  // No-logo monogram: custom text or auto initials, serif or script. Rendered
  // in INK (this is a light ground), not white.
  const monoText = (input.monogramText && input.monogramText.trim()) || brandInitials(brand);
  const monoClass = input.monogramFont === "serif" ? "serif" : "script";

  // On the light paper ground the logo keeps its own colors; only a faint
  // shadow for separation. (No brightness(0) invert(1) whitening here.)
  const logoFilter = "drop-shadow(0 1px 4px rgba(0,0,0,.12))";

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;color:${INK}}
  /* Warm paper mat with a subtle white grain — the frame's ground. */
  .pin{position:relative;width:100%;height:100%;overflow:hidden;background:${PAPER} url('${PAPER_TEX}') center/cover}
  /* Masthead: brand mark, centered, dark-on-light. */
  .mark{position:absolute;top:58px;left:0;right:0;height:152px;z-index:2;display:flex;align-items:center;justify-content:center}
  .logo{max-height:152px;max-width:74%;width:auto;height:auto;display:block;filter:${logoFilter}}
  .monogram{display:flex;align-items:center;justify-content:center;color:${INK}}
  .monogram.serif{width:96px;height:96px;border:2.5px solid ${INK};border-radius:50%;font-family:${typography.headlineFont};font-size:42px;letter-spacing:2px}
  .monogram.script{font-family:'Great Vibes',cursive;font-size:92px;line-height:1}
  /* Inset photo plate: equal side margins, graded to match the set. Styled as
     a CUTOUT window in the paper — a crisp thin cut-line traces the opening,
     and an inner shadow (heavier at the top, where the paper edge catches the
     light) recesses the image below the paper surface, so it reads as inserted
     into the mat rather than resting on top of it. */
  .plate{position:absolute;top:216px;left:84px;right:84px;height:872px;
    background-image:url('${cssUrl(imageUrl)}');background-size:cover;
    background-position:${Math.round((input.focusX ?? 0.5) * 100)}% center;
    filter:${PHOTO_GRADE};
    box-shadow:0 0 0 1px rgba(43,37,32,.32),
      inset 0 0 0 1px rgba(255,255,255,.10),
      inset 0 6px 14px rgba(0,0,0,.34),
      inset 0 -3px 9px rgba(0,0,0,.20),
      inset 7px 0 12px rgba(0,0,0,.16),
      inset -7px 0 12px rgba(0,0,0,.16)}
  /* Caption block beneath the plate — vertically centred in the band below the
     photo so the composition stays balanced (no dead space at the bottom). */
  .caption{position:absolute;top:1104px;left:84px;right:84px;bottom:48px;z-index:2;text-align:center;
    display:flex;flex-direction:column;align-items:center;justify-content:center}
  ${venue.location ? `.kicker{font-size:24px;letter-spacing:5px;text-transform:uppercase;font-weight:700;opacity:.62;margin-bottom:18px}` : ""}
  .name{font-family:${typography.headlineFont};font-size:${nameSize}px;font-weight:${typography.headlineWeight};line-height:1.12;letter-spacing:-.5px;padding-bottom:.16em;
    display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .name em{font-style:italic;font-weight:400}
  ${venue.tagline ? `.tagline{font-family:${typography.headlineFont};font-style:italic;font-size:36px;line-height:1.3;opacity:.78;margin-top:14px;max-width:94%;padding-bottom:.12em;
    display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}` : ""}
  ${cta ? `.cta{margin-top:24px;font-size:25px;letter-spacing:2px;text-transform:uppercase;font-weight:700;opacity:.66}` : ""}
</style></head>
<body><div class="pin">
  <div class="mark">
    ${
      input.hideLogo
        ? ""
        : brandLogo
          ? `<img class="logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
          : `<span class="monogram ${monoClass}">${escapeHtml(monoText)}</span>`
    }
  </div>
  <div class="plate"></div>
  <div class="caption">
    ${venue.location ? `<div class="kicker">${escapeHtml(venue.location)}</div>` : ""}
    <div class="name">${renderTitle(venue.name, input.titleAccent)}</div>
    ${venue.tagline ? `<div class="tagline">${escapeHtml(venue.tagline)}</div>` : ""}
    ${cta ? `<div class="cta">${escapeHtml(cta)} →</div>` : ""}
  </div>
</div></body></html>`;
}
